import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { FormInstance } from "antd";
import { ProvinceConfigurationInjection } from "../../di/ProvinceConfigurationInjection";
import { ProvinceConfigurationListRequestEntity } from "../../domain/entities/ProvinceConfigurationListRequestEntity";
import { ProvinceConfigurationFormRequestEntity } from "../../domain/entities/ProvinceConfigurationFormRequestEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IProvinceConfigurationObservedState } from "../interfaces/IProvinceConfiguration";
import { BaseAdministrativeRegionModel } from "app/based/baseApiModel/baseAddress/BaseAdministrativeRegionModel";

// ─── Input ────────────────────────────────────────────────────────────────────

class ProvinceConfigurationInput implements Disposable {
  public fetchList = new BehaviorSubject<ProvinceConfigurationListRequestEntity>({
    page: 0,
    limit: 10,
  });
  public fetchDetail = new BehaviorSubject<string>("");
  public createProvince = new BehaviorSubject<ProvinceConfigurationFormRequestEntity>({});
  public updateProvince = new BehaviorSubject<ProvinceConfigurationFormRequestEntity>({});
  public deleteProvince = new BehaviorSubject<string>("");
  public fetchRegions = new BehaviorSubject<void>(undefined);

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.createProvince.complete();
    this.updateProvince.complete();
    this.deleteProvince.complete();
    this.fetchRegions.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class ProvinceConfigurationOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<
    "create" | "update" | "delete" | null
  >(null);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.actionSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class ProvinceConfigurationViewModel extends BasedObservedViewModel<
  ProvinceConfigurationInput,
  ProvinceConfigurationOutput,
  IProvinceConfigurationObservedState
> {
  constructor() {
    super(new ProvinceConfigurationInput(), new ProvinceConfigurationOutput());
  }

  private useCase = ProvinceConfigurationInjection.getUseCase();

  protected override createObservedState(): IProvinceConfigurationObservedState {
    return {
      loading: false,
      formLoading: false,
      detailLoading: false,
      list: [],
      detail: {},
      paging: { page: 0, limit: 10, total: 0 },
      regionOptions: [],
    };
  }

  public buildTableQuery(
    pagination: any,
    sorter: any,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: any,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    let order = prevParams?.order;
    if (sorter && sorter.field && sorter.order) {
      const direction = sorter.order === "ascend" ? "ASC" : "DESC";
      order = `${sorter.field} ${direction}`;
    }

    const query = {
      ...prevParams,
      ...formValues,
      page: (pagination?.current ?? 1) - 1,
      limit: pagination?.pageSize ?? 10,
      order,
    };

    return {
      query,
    };
  }

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.getProvinceConfigurationList(params);
        if (res) {
          this.setObservedState({
            list: res.data || [],
            paging: res.pagination || {
              page: params.page || 0,
              limit: params.limit || 10,
              total: 0,
            },
          });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch detail
    this.skipFirstInitial(this.input.fetchDetail, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.useCase.getProvinceConfigurationDetail(id);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    // Create province
    this.skipFirstInitial(this.input.createProvince, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.createProvinceConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Update province
    this.skipFirstInitial(this.input.updateProvince, async (body) => {
      if (!body || Object.keys(body).length === 0) return;
      try {
        this.setObservedState({ formLoading: true });
        const res = await this.useCase.updateProvinceConfiguration(body);
        if (res?.data) {
          this.output.actionSuccess.next("update");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ formLoading: false });
      }
    });

    // Delete province
    this.skipFirstInitial(this.input.deleteProvince, async (id) => {
      if (!id) return;
      try {
        this.setObservedState({ loading: true });
        const res = await this.useCase.deleteProvinceConfiguration(id);
        if (res) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch regions
    this.skipFirstInitial(this.input.fetchRegions, async () => {
      try {
        const res = await this.useCase.getRegionList();
        if (res) {
          const regionOptions = (res || [])?.map((item: BaseAdministrativeRegionModel) => ({
            label: item?.name,
            value: item?.id,
          }));
          this.setObservedState({ regionOptions });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(error?.message || strings.GlobalMessage.Error);
      }
    });
  }
}
